import React from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  InputAdornment,
} from "@mui/material";
import { CreditCard, Person, Save } from "@mui/icons-material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  useReadPaymentSettingsQuery,
  useUpdatePaymentSettingsMutation,
} from "api/endpoints/settings";
import { toast } from "react-toastify";

const validationSchema = Yup.object({
  cardNumber: Yup.string()
    .test(
      "is-valid-card-number",
      "شماره کارت باید 16 رقم باشد",
      function (value) {
        if (!value) return false;
        const digitsOnly = value.replace(/-/g, "");
        return /^\d{16}$/.test(digitsOnly);
      }
    )
    .required("شماره کارت الزامی است"),
  cardOwnerName: Yup.string()
    .min(2, "نام صاحب کارت باید حداقل 2 کاراکتر باشد")
    .required("نام صاحب کارت الزامی است"),
});

export const SettingsCardNumbersComponent = ({
  embedded = false,
}: {
  embedded?: boolean;
}) => {
  const { data: settings, isLoading, error } = useReadPaymentSettingsQuery();
  const [updateSettings, { isLoading: updating, error: updateError }] =
    useUpdatePaymentSettingsMutation();

  const formatCardNumber = (value: string): string => {
    const digitsOnly = value.replace(/\D/g, "");
    return digitsOnly.replace(/(\d{4})(?=\d)/g, "$1-");
  };

  const handleCardNumberChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    setFieldValue: (field: string, value: string) => void
  ) => {
    const formattedValue = formatCardNumber(e.target.value);
    const finalValue =
      formattedValue.endsWith("-") && formattedValue.length > 4
        ? formattedValue.slice(0, -1)
        : formattedValue;
    setFieldValue("cardNumber", finalValue);
  };

  const handleSubmit = async (values: {
    cardNumber: string;
    cardOwnerName: string;
  }) => {
    try {
      await toast.promise(
        updateSettings({ payload: values }).unwrap(),
        {
          error: {
            render:
              "هنگام بروزرسانی تنظیمات مشکلی بوجود آمد، لطفا با پشتیبانی ارتباط برقرار نمایید.",
          },
          pending: {
            render: "درحال بروزرسانی تنظیمات، لطفا منتظر بمانید...",
            autoClose: 5000,
          },
          success: "تنظیمات با موفقیت بروزرسانی شد.",
        }
      );
    } catch (err) {
      console.error("Failed to update settings:", err);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        خطا در بارگذاری تنظیمات
      </Alert>
    );
  }

  return (
    <Box p={embedded ? 0 : 3}>
      {!embedded && (
        <Typography variant="h4" gutterBottom>
          شماره کارت
        </Typography>
      )}

      <Paper sx={{ p: 3 }}>
        {updateError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            خطا در به‌روزرسانی تنظیمات
          </Alert>
        )}

        <Formik
          initialValues={{
            cardNumber: settings?.cardNumber
              ? formatCardNumber(settings.cardNumber)
              : "",
            cardOwnerName: settings?.cardOwnerName || "",
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            handleSubmit({
              cardNumber: values.cardNumber.replace(/-/g, ""),
              cardOwnerName: values.cardOwnerName,
            });
          }}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
          }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        اطلاعات کارت بانکی
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            name="cardNumber"
                            label="شماره کارت"
                            value={values.cardNumber}
                            onChange={(e) =>
                              handleCardNumberChange(e, setFieldValue)
                            }
                            onBlur={handleBlur}
                            error={
                              touched.cardNumber && Boolean(errors.cardNumber)
                            }
                            helperText={touched.cardNumber && errors.cardNumber}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CreditCard />
                                </InputAdornment>
                              ),
                            }}
                            placeholder="1234-5678-9012-3456"
                            dir="ltr"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            name="cardOwnerName"
                            label="نام صاحب کارت"
                            value={values.cardOwnerName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.cardOwnerName &&
                              Boolean(errors.cardOwnerName)
                            }
                            helperText={
                              touched.cardOwnerName && errors.cardOwnerName
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Person />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={<Save className="icon-btn" />}
                      disabled={updating}
                    >
                      {updating ? "در حال ذخیره..." : "ذخیره تنظیمات"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};
